import { DualPercentSplitSlider } from '@/components/planning/DualPercentSplitSlider';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  equalSplitPercents,
  normalizeToHundred,
  type PercentEntry,
} from '@/features/planning/percentAllocation';
import { useLanguage } from '@/i18n/language-hooks';

export interface AllocationPercentEntry {
  id: string;
  label: string;
  percent: number;
}

interface AllocationPercentEditorProps {
  entries: AllocationPercentEntry[];
  onBulkChange: (percents: number[]) => void;
  totalLabel?: (total: number, remaining: number) => string;
}

export function AllocationPercentEditor({
  entries,
  onBulkChange,
  totalLabel,
}: AllocationPercentEditorProps) {
  const { t } = useLanguage();

  if (entries.length === 0) return null;

  const total = entries.reduce((sum, entry) => sum + entry.percent, 0);
  const remaining = 100 - total;
  const isBalanced = Math.abs(remaining) <= 0.01;

  const applyChange = (changedIndex: number, newValue: number) => {
    const clamped = Math.max(0, Math.min(100, newValue));
    onBulkChange(entries.map((entry, index) => (index === changedIndex ? clamped : entry.percent)));
  };

  const toBase = (): PercentEntry[] => entries.map((e) => ({ id: e.id, percent: e.percent }));

  const handleNormalize = () => {
    onBulkChange(normalizeToHundred(toBase()).map((item) => item.percent));
  };

  const handleMakeEqual = () => {
    onBulkChange(equalSplitPercents(toBase()).map((item) => item.percent));
  };

  const statusMessage =
    totalLabel?.(total, remaining) ??
    (isBalanced
      ? t('planning.succession.allocationTotal', { percent: total.toFixed(1) })
      : remaining > 0
        ? t('planning.succession.allocationRemaining', {
            allocated: total.toFixed(1),
            remaining: remaining.toFixed(1),
          })
        : t('planning.succession.allocationOver', { percent: total.toFixed(1) }));

  if (entries.length === 1) {
    const [entry] = entries;
    return (
      <div className='flex items-center gap-3'>
        <span className='min-w-0 flex-1 truncate text-sm'>{entry.label}</span>
        <span className='text-fin-white w-12 text-right text-sm font-medium tabular-nums'>
          100%
        </span>
      </div>
    );
  }

  if (entries.length === 2) {
    const [first, second] = entries;
    return (
      <DualPercentSplitSlider
        leftLabel={first.label}
        rightLabel={second.label}
        leftPercent={first.percent}
        onChange={(left, right) => onBulkChange([left, right])}
      />
    );
  }

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <p
          className={`text-sm tabular-nums ${isBalanced ? 'text-muted-foreground' : 'text-fin-warning'}`}
        >
          {statusMessage}
        </p>
        {isBalanced ? (
          <Button type='button' variant='outline' size='sm' onClick={handleMakeEqual}>
            {t('planning.succession.makeEqualAllocation')}
          </Button>
        ) : (
          <Button type='button' variant='outline' size='sm' onClick={handleNormalize}>
            {t('planning.succession.normalizeAllocation')}
          </Button>
        )}
      </div>

      {entries.map((entry, index) => (
        <div key={entry.id} className='flex items-center gap-3'>
          <span className='min-w-0 flex-1 truncate text-sm'>{entry.label}</span>
          <Slider
            value={[entry.percent]}
            min={0}
            max={100}
            step={1}
            className='max-w-md flex-1'
            onValueChange={([value]) => applyChange(index, value)}
            aria-label={entry.label}
          />
          <span className='text-fin-white w-12 text-right text-sm font-medium tabular-nums'>
            {Math.round(entry.percent)}%
          </span>
        </div>
      ))}
    </div>
  );
}
